import React, { useCallback, useEffect, useMemo, useState } from "react";
import LaddaButton from "../../../components/buttons/laddaButton";
import SimpleTable from "../../../components/tables/simpleTable";
import { technicalFeaturesColumns } from "../columns";
import { icons } from "../../../constants";
import {
  callActionApiAsync,
  callApiAsync,
  getBiomedicalEquipmentModelFeatureData,
  postDeleteBiomedicalEquipmentModelFeature
} from "../../../services/apiService";
import VerificationModal from "../../../components/modals/verificationModal";
import ModelFeatureModal from "./ModelFeatureModal";

const ModelTechnicalFeaturesTab = ({
  modelRow,
  userId,
  isCatalogAdmin,
  pushToast,
  equipmentCount
}) => {
  const [featureRows, setFeatureRows] = useState([]);
  const [featureOptions, setFeatureOptions] = useState([]);
  const [openFeatureModal, setOpenFeatureModal] = useState(false);
  const [selectedFeatureRow, setSelectedFeatureRow] = useState(null);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [openDeleteVerification, setOpenDeleteVerification] = useState(false);

  const selectedModelId = modelRow?.value ? Number(modelRow.value) : null;
  const affectedEquipmentCount = Number(equipmentCount ?? 0);

  const loadFeatureData = useCallback(() => {
    if (!selectedModelId || !userId) {
      setFeatureRows([]);
      setFeatureOptions([]);
      return;
    }

    callApiAsync(
      () => getBiomedicalEquipmentModelFeatureData(selectedModelId, userId),
      (data) => {
        setFeatureRows(data?.table || []);
        setFeatureOptions(data?.list || []);
      }
    );
  }, [selectedModelId, userId]);

  useEffect(() => {
    if (!selectedModelId) {
      setFeatureRows([]);
      setFeatureOptions([]);
      return;
    }

    loadFeatureData();
  }, [loadFeatureData, selectedModelId]);

  const handleRequestDeleteFeature = useCallback(
    (row) => {
      if (!row?.id || !userId) return;
      setRowPendingDelete(row);
      setOpenDeleteVerification(true);
    },
    [userId]
  );

  const handleCloseDeleteVerification = useCallback(() => {
    setOpenDeleteVerification(false);
    setRowPendingDelete(null);
  }, []);

  const handleConfirmDeleteFeature = useCallback(async () => {
    if (!rowPendingDelete?.id || !userId) return;

    await callActionApiAsync(
      () =>
        postDeleteBiomedicalEquipmentModelFeature({
          id: rowPendingDelete.id,
          userId
        }),
      (data) => {
        loadFeatureData();
        handleCloseDeleteVerification();
        pushToast(
          "success",
          "Caracteristicas tecnicas",
          `La plantilla tecnica del modelo se actualizo y se sobrescribieron las caracteristicas de ${
            data?.updatedEquipment ?? 0
          } equipo(s).`
        );
      },
      (error) => {
        pushToast(
          "error",
          "Caracteristicas tecnicas",
          typeof error === "string" && error
            ? error
            : "No se pudo eliminar la caracteristica del modelo."
        );
      }
    );
  }, [
    handleCloseDeleteVerification,
    loadFeatureData,
    pushToast,
    rowPendingDelete,
    userId
  ]);

  const tableButtons = useMemo(
    () =>
      isCatalogAdmin
        ? [
            {
              type: "icon",
              icon: icons.editYellow,
              onClick: (row) => {
                setSelectedFeatureRow(row);
                setOpenFeatureModal(true);
              }
            },
            {
              type: "icon",
              icon: icons.trash,
              onClick: handleRequestDeleteFeature
            }
          ]
        : [],
    [handleRequestDeleteFeature, isCatalogAdmin]
  );

  if (!selectedModelId) {
    return (
      <div className="admin-catalog-detail-card">
        <p className="admin-catalog-detail-value admin-catalog-hint">
          Seleccione una marca y un modelo para administrar las caracteristicas
          tecnicas del modelo.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="admin-catalog-detail-card">
        <div className="HContainer j-between">
          <div className="mr-20 IV">
            <p className="admin-catalog-detail-label">Modelo seleccionado</p>
            <p className="admin-catalog-detail-value">
              {modelRow?.name || "-"}
            </p>
          </div>
          <div>
            <p className="admin-catalog-detail-label">Equipos afectados</p>
            <p className="admin-catalog-detail-value">
              {affectedEquipmentCount}
            </p>
          </div>
        </div>
        <p className="admin-catalog-hint">
          Cada cambio en esta plantilla tecnica sobrescribe las caracteristicas
          de todos los equipos asociados a este modelo y tambien queda como base
          para los nuevos equipos.
        </p>
      </div>

      <div className="mt-25">
        <div className="IHContainer j-space-between mb-15">
          <p className="createRist-title ml-0">
            Caracteristicas tecnicas del modelo
          </p>
          {isCatalogAdmin && (
            <LaddaButton
              width={"40px"}
              fontSize={"12px"}
              height={"40px"}
              variant="contained"
              margin={"0"}
              onClick={() => {
                setSelectedFeatureRow(null);
                setOpenFeatureModal(true);
              }}
            >
              <img className="btn-icon" src={icons.plusIcon} alt="icon" />
            </LaddaButton>
          )}
        </div>

        <SimpleTable
          data={featureRows}
          columns={technicalFeaturesColumns}
          scrollHeight={"280px"}
          text={"Caracteristicas tecnicas del modelo"}
          buttons={tableButtons}
        />
      </div>

      <ModelFeatureModal
        open={openFeatureModal}
        close={() => {
          setOpenFeatureModal(false);
          setSelectedFeatureRow(null);
        }}
        selectedRow={selectedFeatureRow}
        modelId={selectedModelId}
        userId={userId}
        featureOptions={featureOptions}
        setFeatureOptions={setFeatureOptions}
        affectedEquipmentCount={affectedEquipmentCount}
        onSaved={(data) => {
          loadFeatureData();
          pushToast(
            "success",
            "Caracteristicas tecnicas",
            `La plantilla tecnica del modelo se actualizo y se sobrescribieron las caracteristicas de ${
              data?.updatedEquipment ?? 0
            } equipo(s).`
          );
        }}
      />

      <VerificationModal
        open={openDeleteVerification}
        handleClose={handleCloseDeleteVerification}
        alertText={
          affectedEquipmentCount > 0
            ? `Esta accion eliminara la caracteristica "${
                rowPendingDelete?.featureName || ""
              }" de la plantilla y sobrescribira las caracteristicas tecnicas de ${affectedEquipmentCount} equipo(s) asociado(s) a este modelo. Desea continuar?`
            : `Esta accion eliminara la caracteristica "${
                rowPendingDelete?.featureName || ""
              }" de la plantilla. Actualmente no hay equipos asociados para sobrescribir. Desea continuar?`
        }
        onConfirm={handleConfirmDeleteFeature}
        onCancel={handleCloseDeleteVerification}
        iconSeverity="warning"
        yesNo
      />
    </>
  );
};

export default ModelTechnicalFeaturesTab;
